import { Button, Grid, Message, Modal } from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useState } from "react";
import { useUpdateClientMembership } from "../api/clients";
import { usePlatforms } from "../api/platforms";
import type { ClientListItem } from "../api/types";

// Connects a standalone client to a platform. It is the client's own change of
// platform (the same call as the platform field in the edit drawer): the user
// needs to be allowed to change both the client and the platform, and the
// message says when not.
export function ClientConnectModal({
  client,
  open,
  onClose,
  onConnected,
}: {
  client: ClientListItem;
  open: boolean;
  onClose: () => void;
  /** Called with the platform's id once the client is in it. */
  onConnected: (platformId: string) => void;
}) {
  const { data: platforms = [] } = usePlatforms();
  const connect = useUpdateClientMembership(client.id);
  const [platformId, setPlatformId] = useState("");
  const options = platforms.map((p) => ({ value: p.id, label: p.name }));

  function close() {
    if (connect.isPending) return;
    onClose();
    connect.reset();
    setPlatformId("");
  }

  async function confirm() {
    if (!platformId) return;
    try {
      await connect.mutateAsync(platformId);
      onConnected(platformId);
    } catch {
      // The error shows from the mutation state.
    }
  }

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      header="Connect to platform"
      width={480}
    >
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          Pick the platform <strong>{client.name}</strong> belongs to. It is
          then viewed from that platform.
        </p>
        <Select
          label="Platform"
          placeholder={
            options.length === 0
              ? "No platforms available"
              : "Select a platform…"
          }
          options={options}
          value={options.find((o) => o.value === platformId) ?? null}
          onChange={(opt) =>
            setPlatformId((opt as { value: string } | null)?.value ?? "")
          }
          isDisabled={options.length === 0}
        />
        {connect.error && (
          <Message state="alert" header="Could not connect the client">
            {connect.error instanceof Error
              ? connect.error.message
              : "Something went wrong."}
          </Message>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={close}
            state={connect.isPending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={platformId && !connect.isPending ? confirm : undefined}
            state={!platformId || connect.isPending ? "inactive" : undefined}
          >
            {connect.isPending ? "Connecting…" : "Connect"}
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}
